import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ReservationPage.css';
import { useLanguage } from '../i18n/LanguageContext';
import { localizeEntity } from '../i18n/localizeData';
import { useAuth } from './AuthContext';


const API_URL = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';
const RESERVATION_PREFILL_KEY = 'kmq-reservation-prefill-v1';

function loadSavedReservationPrefill() {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.sessionStorage.getItem(RESERVATION_PREFILL_KEY) || 'null');
  } catch {
    return null;
  }
}

function persistReservationPrefill(value) {
  if (typeof window === 'undefined') return;
  if (!value) {
    window.sessionStorage.removeItem(RESERVATION_PREFILL_KEY);
    return;
  }
  window.sessionStorage.setItem(RESERVATION_PREFILL_KEY, JSON.stringify(value));
}

function ReservationPage() {
  const { professionalId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { signupPatient } = useAuth();

  const { serviceId, date, startTime } = location.state || {};
  const [professional, setProfessional] = useState(null);
  const [service, setService] = useState(null);

  const [formData, setFormData] = useState(() => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    birthDate: '',
    postalCode: '',
    notes: '',
    ...(loadSavedReservationPrefill() || {})
  }));

  const [showRecap, setShowRecap] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    axios.get(`${API_URL}/users/${professionalId}`)
      .then(res => setProfessional(localizeEntity(res.data, language)))
      .catch(err => console.error('Erreur fetching professional:', err));

    axios.get(`${API_URL}/services`)
      .then(res => {
        const found = res.data.find(s => s.service_id === serviceId);
        setService(found ? localizeEntity(found, language) : null);
      })
      .catch(err => console.error('Erreur fetching service:', err));
  }, [language, professionalId, serviceId]);

  useEffect(() => {
    persistReservationPrefill(formData);
  }, [formData]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isValidCanadianPostalCode = (postalCode) => {
    const regex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    return regex.test(postalCode);
  };

  const handleRecap = (e) => {
    e.preventDefault();
    if (formData.postalCode && !isValidCanadianPostalCode(formData.postalCode)) {
      alert(`❌ ${t('booking.invalidPostalCode')}`);
      return;
    }
    setShowRecap(true);
  };

  const handleConfirm = async () => {
    try {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        alert(t('booking.missingIdentity'));
        return;
      }
  
      const userPayload = {
        first_name: formData.firstName?.trim(),
        last_name: formData.lastName?.trim(),
        email: formData.email?.trim(),
        phone: formData.phone?.trim() || null, 
        user_type: 'patient',
        zone_id: null
      };
  
      console.log("🔍 Payload envoyé :", userPayload);
  
      const userRes = await axios.post(`${API_URL}/users`, userPayload);
      console.log("✅ Réponse backend :", userRes.data);
      const userId = userRes?.data?.user_id;
      if (!userId) throw new Error("Échec récupération user_id");
  
      await axios.post(`${API_URL}/patients`, { user_id: userId });
  
      await axios.put(`${API_URL}/patients/${userId}`, {
        address: formData.address,
        birth_date: formData.birthDate,
        postal_code: formData.postalCode || null
      });
  
      const duration = service?.duration_minutes || 30;
      const parsedDate = new Date(date);
      const dateOnly = parsedDate.toISOString().split("T")[0];
      const normalizedTime = startTime.length === 5 ? `${startTime}:00` : startTime;
      const startDateTime = new Date(`${dateOnly}T${normalizedTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
  
      const startIso = startDateTime.toISOString();
      const endIso = endDateTime.toISOString();
  
      const appointmentData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        birth_date: formData.birthDate,
        postal_code: formData.postalCode || null,
        notes: formData.notes || null,
        professional_id: professionalId,
        service_id: serviceId,
        start_time: startIso,
        end_time: endIso,
        mode: service?.mode || 'in_person'
      };
  
      const billingInfo = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        address: formData.address
      };
  
      navigate('/paiement', {
        state: {
          appointmentData,
          billingInfo,
          servicePriceCents: service?.price_cents || 5000
        }
      });
      persistReservationPrefill(null);
  
    } catch (err) {
      console.error("❌ Erreur réservation invité :", err);
      alert(t('booking.bookingError'));
    }
  };

  const handleCreateAccount = () => {
    persistReservationPrefill(formData);
    signupPatient({ redirectTo: window.location.href });
  };

  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const formattedDate = date ? new Date(date).toLocaleDateString(locale) : '';

  return (
    <div className="reservation-container">
      <button className="btn-back" onClick={() => navigate(-1)}>{`← ${t('common.back')}`}</button>
      <div className="reservation-card">
        <h2 className="reservation-title">{t('booking.reserveSlot')}</h2>

        <div className="reservation-info">
          <p><strong>{t('booking.appointmentDate')}:</strong> {formattedDate}</p>
          <p><strong>{t('booking.appointmentTime')} :</strong> {startTime}</p>
          <p><strong>{t('booking.professional')} :</strong> {professional ? `${professional.first_name} ${professional.last_name}` : t('common.loading')}</p>
          <p><strong>{t('booking.service')} :</strong> {service ? service.name : t('common.loading')}</p>
        </div>

        {!showRecap ? (
          <form onSubmit={handleRecap} className="reservation-form">
            <input type="text" name="lastName" placeholder={t('booking.lastName')} value={formData.lastName} onChange={handleChange} required />
            <input type="text" name="firstName" placeholder={t('booking.firstName')} value={formData.firstName} onChange={handleChange} required />
            <input type="email" name="email" placeholder={t('booking.email')} value={formData.email} onChange={handleChange} required />
            <input type="tel" name="phone" placeholder={t('booking.phone')} value={formData.phone} onChange={handleChange} />
            <input type="text" name="address" placeholder={t('booking.address')} value={formData.address} onChange={handleChange} />
            <input type="text" name="postalCode" placeholder={t('booking.postalCode')} value={formData.postalCode} onChange={handleChange} />
            <label>
              <span>{t('booking.birthDate')} :</span>
              <input type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} required />
            </label>
            <textarea name="notes" placeholder={t('booking.notesPlaceholder')} value={formData.notes} onChange={handleChange} rows={3} />
            <div className="reservation-buttons d-flex justify-content-between mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleCreateAccount}
                >
                  {t('booking.createAccount')}
                </button>

                <button type="submit" className="btn btn-primary">
                  {t('booking.continueAsGuest')}
                </button>
              </div>
            </form>
        ) : (
          <div className="reservation-recap">
            <h3>{t('booking.summary')}</h3>
            <p><strong>{t('booking.lastName')} :</strong> {formData.lastName}</p>
            <p><strong>{t('booking.firstName')} :</strong> {formData.firstName}</p>
            <p><strong>{t('booking.email')} :</strong> {formData.email}</p>
            <p><strong>{t('booking.phone')} :</strong> {formData.phone || t('common.noneProvided')}</p>
            <p><strong>{t('booking.address')} :</strong> {formData.address || t('common.noneProvidedFeminine')}</p>
            <p><strong>{t('booking.postalCode')} :</strong> {formData.postalCode || t('common.noneProvided')}</p>
            <p><strong>{t('booking.birthDate')} :</strong> {formData.birthDate || t('common.noneProvidedFeminine')}</p>
            <p><strong>{t('booking.notes')} :</strong> {formData.notes || t('booking.noNotes')}</p>

            <button onClick={handleConfirm} className="btn-confirm">{t('booking.payAndConfirm')}</button>
            <button onClick={() => setShowRecap(false)} className="btn-edit">{t('booking.editInformation')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReservationPage;
